const version = require('../package.json').version

import Vue from 'vue'
import gql from 'graphql-tag'
import _has from 'lodash/has'
import _pick from 'lodash/pick'
import BrowserCookies from 'js-cookie'
import { v4 as uuidv4 } from 'uuid'

import ComponentWrapper from './ComponentWrapper.vue'

Vue.config.productionTip = false

/**
 * Globally register async components for Edgewise Elements.
 *
 * This dynamic import syntax will instruct Webpack to
 * automatically split our built code into bundles which
 * are loaded over async requests when used.
 * See: https://vuejs.org/v2/guide/components-dynamic-async.html
 *
 * Development HTML wrapper located at /public/index.html
 */

Vue.component('ProjectContactForm', () =>
  import('./webcomponents/ProjectContactForm')
)
Vue.component('ProjectDocuments', () =>
  import('./webcomponents/ProjectDocuments')
)
Vue.component('ProjectFloorPlansGrid', () =>
  import('./webcomponents/ProjectFloorPlansGrid')
)
Vue.component('ProjectImageGallery', () =>
  import('./webcomponents/ProjectImageGallery')
)
Vue.component('ProjectInventoryGrid', () =>
  import('./webcomponents/ProjectInventoryGrid')
)
Vue.component('ProjectInventoryList', () =>
  import('./webcomponents/ProjectInventoryList')
)
Vue.component('ProjectOpenHouseFlash', () =>
  import('./webcomponents/ProjectOpenHouseFlash')
)
Vue.component('UnitBuyNowButton', () =>
  import('./webcomponents/UnitBuyNowButton')
)

/**
 */
const Edgewise = (opts = {}) => {
  // opts are assigned to Edgewise.prototype,
  // effectively functioning as global config.
  Edgewise.prototype = Edgewise.prototype
    ? {
        debug: opts.debug !== undefined ? opts.debug : false,
        ...Edgewise.prototype,
        ...opts
      }
    : opts

  let _trackerId = null
  let _analyticsOpts = null // eslint-disable-line

  const debug = Edgewise.prototype.debug
  if (debug) console.log(`[edgewise.js] version: v${version}`)

  const fingerprint = getFingerprint()
  if (debug) console.log(`[edgewise.js] fingerprint: ${fingerprint}`)
  const connectionPromise = connect({ fingerprint }, opts)

  if (debug) console.log(`[edgewise.js] config`, Edgewise.prototype)

  assignUtmSessionData(window.location.search)

  return {
    version,

    /**
     * Edgewise Components
     * Programatic versions of Edgewise WebComponents
     *
     * The `projectId` is required on all components. If not explicitly set
     * on the component, it will be passed in from the components/1 config,
     * then falling back to global config.
     */
    components: (opts = {}) => {
      const componentsProjectId =
        opts.projectId !== undefined
          ? opts.projectId
          : Edgewise.prototype.projectId || null

      const componentsTheme =
        opts.theme !== undefined
          ? opts.theme
          : Edgewise.prototype.theme || 'vanilla'

      const componentsDebug = opts.debug !== undefined ? opts.debug : debug
      if (componentsDebug) console.log(`[edgewise.js] components init`, opts)

      return {
        create: (elName, data) => {
          const mergedData = {
            debug: componentsDebug,
            projectId: componentsProjectId,
            theme: componentsTheme,
            ...opts,
            ...data
          }

          return createEdgewiseComponent(elName, mergedData)
        }
      }
    },

    /**
     * Edgewise Analytics
     * NOTE: we do not return a function (a la components) because it would
     * need to be async, and I'm not sure a lot of devs fully understand
     * that concept. At least with `push/3` (which is also async), they don'
     * really need to know what's going on.
     */
    analytics: {
      /**
       * Initialize the tracker by assigning the `tracker_id` to the lobby
       * channel's presence. Can be explicitly overwritten per `push`.
       */
      init: async (trackerId, opts = {}) => {
        _trackerId = trackerId
        _analyticsOpts = opts

        const connection = await connectionPromise
        connection.update({ tracker_id: trackerId })
        if (debug)
          console.log(`[edgewise.js] analytics init: ${trackerId}`, { opts })
        return trackerId
      },

      /**
       */
      push: async (eventName, eventProps = {}, opts = {}) => { //eslint-disable-line
        const connection = await connectionPromise

        return _trackerId
          ? (() => {
              if (debug)
                console.log(
                  `[edgewise.js] analytics track`,
                  eventName,
                  eventProps,
                  opts
                )
              const standardProps = buildPageProps(document)
              const props = {
                ...standardProps,
                ...eventProps
              }
              connection.track(eventName, props, opts)
            })()
          : (() => {
              console.error(
                '[edgewise.js] analytics not initialized (missing `trackerId`)'
              )
              return null
            })()
      }
    }
  }
}

/**
 * Exports for all of the edgewise.js library (analytics, components, etc.).
 */
export default Edgewise

/**
 * Create the Edgewise Component prototype.
 * The underlying Vue instance is assigned to `_vue`.
 * We expose Vue's `$mount()` method as `mount()`.
 * We expose the Vue's `$root.$on()` simply as `on()`.
 */
function EdgewiseComponent(vueInstance) {
  this._vue = vueInstance

  this.mount = (selector) => {
    this._vue.$mount(selector)
    return this
  }

  /**
   * In lib, it seems we are only able listen on `this.$root.$emit` events.
   * In WC, it seems $root is ignored, and we must listen on `this.$emit`.
   * So we essentially emit on both (one for lib, and one for wc)
   */
  this.on = (event, handler) => {
    this._vue.$root.$on(event, handler)
    return this
  }

  return this
}

/**
 * Create an Edgewise Component.
 */
const createEdgewiseComponent = (elName, data) => {
  const debug = data.debug !== undefined ? data.debug : false

  if (debug) {
    console.log('[edgewise.js] create component', { elName, data, debug })
  }

  const vueInstance = new Vue({
    render: (h) =>
      h(ComponentWrapper, {
        props: {
          elName,
          elData: data
        }
      })
  })

  return new EdgewiseComponent(vueInstance)
}

/**
 * First looks for the `edgewise_fingerprint` cookie.
 * If not set, we create a UUID and set the cookie for future use.
 * TODO: consider using fingerprint.js for cross-site tracking.
 */
const getFingerprint = () => {
  const fingerprintCookie = BrowserCookies.get('edgewise_fingerprint') || null

  return fingerprintCookie
    ? fingerprintCookie
    : (() => {
        const uuid = uuidv4().replaceAll('-', '').slice(0, 24)
        BrowserCookies.set('edgewise_fingerprint', uuid, { expires: 1825 })
        return uuid
      })()
}

/**
 * Initialize the socket connection, and join the lobby channel.
 * The lobby is used for analytics (tracking).
 */
const connect = async (params, opts) => {
  const debug = opts.debug !== undefined ? opts.debug : false

  const { createClient } = await import('./gql-client')
  const gqlClient = createClient({
    headers: {
      'edgewise-client-name': 'JavaScriptSDK',
      'edgewise-client-id': version
    }
  })
  const viewer = await fetchViewer({ client: gqlClient })

  const socketParams = {
    ...params,
    token: viewer.token,
    ipAddress: viewer.ipAddress,
    location: { ...window.location },
    debug
  }

  const { default: socket } = await import('./libs/socket')
  const _socket = socket(socketParams)
  _socket.connect().onOpen(() => {
    if (!_has(_socket, 'channels[lobby].channel')) {
      if (debug) console.log('[edgewise.js] connected.')
      _socket.join('lobby', { location: { ...window.location } })
    } else {
      if (debug) console.log('[edgewise.js] reconnected.')
    }
  })

  return _socket
}

/**
 * Fetch an anonymous user token for
 * use with socket connections.
 */
const fetchViewer = async ({ client }) => {
  const { viewer } = await client.request(gql`
    query SDKViewer {
      viewer {
        token
        ipAddress
      }
    }
  `)

  return viewer
}

/**
 * Build standard props to sent along with custom properties.
 */
const buildPageProps = (document) => {
  return {
    _location_href: document.location.href,
    _location_origin: document.location.origin,
    _location_host: document.location.host,
    _location_hostname: document.location.hostname,
    _location_pathname: document.location.pathname,
    _referrer: document.referrer,
    _title: document.title
  }
}

/**
 * Capture any UTM parameters and assigns them to session storage
 * for later submission in `ProjectContactForm`.
 */
const assignUtmSessionData = (locationSearch) => {
  const qs = require('qs')

  if (locationSearch) {
    const queryString = locationSearch.slice(1)
    const searchParams = qs.parse(queryString)
    const searchParamKeys = Object.keys(searchParams)
    const utmKeys = searchParamKeys.filter((key) => key.startsWith('utm_'))

    if (utmKeys.length > 0) {
      const utmSessionData = _pick(searchParams, utmKeys)
      sessionStorage.setItem('utmSessionData', JSON.stringify(utmSessionData))
    }
  }
}

/**
 * Dev only, instantiate one / many components.
 */
if (process.env.NODE_ENV === 'development') {
  const edgewise = Edgewise({
    debug: true,
    // projectId: 'the-residences-at-foundry-place',
    projectId: '427',
    theme: 'squarespace'
  })

  const components = edgewise.components({
    // debug: true
    // projectId: '427',
    // theme: 'edgewise',
    // theme: 'squarespace'
  })

  //
  // tracking
  //
  // edgewise.analytics.init('UFFNGGKHUU')
  // edgewise.analytics.push('sdk_test')

  //
  // components
  //

  // ProjectOpenHouseFlash
  // components
  //   .create(
  //     "ProjectOpenHouseFlash",
  //     { projectId: "8", theme: 'edgewise' }
  //   )
  //   .mount('#flash')

  /**
   * ProjectImageGallery
   */
  // components
  //   .create('ProjectImageGallery', {
  //     type: 'SLIDESHOW_REEL'
  //     // type: 'GRID_SIMPLE'
  //   })
  //   .mount('#component')

  /**
   * ProjectInventoryGrid
   */
  components.create('ProjectInventoryGrid').mount('#component')

  /**
   * ProjectInventoryList
   */
  // components.create('ProjectInventoryList').mount('#component')

  /**
   * ProjectContactForm
   */
  // components.create('ProjectContactForm').mount('#component')

  // // ProjectContactForm (alt)
  // Edgewise()
  //   .components()
  //   .create('ProjectContactForm', {
  //     projectId: '524',
  //     confirmationDisabled: true,
  //     theme: 'squarespace'
  //   })
  // .on('created', (event) => {
  //   console.log('ProjectContactForm created', { event })
  // })
  // .on('mounted', (event) => {
  //   console.log('ProjectContactForm mounted', { event })
  // })
  //   .on('submit', (event) => {
  //     console.log('ProjectContactForm submit', { event })
  //     window.location = 'https://google.com'
  //   })
  //   .mount('#contact')

  /**
   * ProjectFloorPlansGrid
   */
  // components
  //   .create('ProjectFloorPlansGrid', { xprojectId: 427 })
  //   .mount('#component')

  // // ProjectDocuments
  // const component =
  //   components.create(
  //     "ProjectDocuments",
  //     {
  //       projectId: "141",
  //       theme: 'edgewise'
  //     }
  //   )

  // // UnitBuyNowButton
  // const component =
  //   components.create(
  //     "UnitBuyNowButton",
  //     {
  //       projectId: "141",
  //       unitId: "1419"
  //     }
  //   )

  // console.log({ component })
  // component.mount("#app")

  // element.on("test", () => {
  //   console.log("edgwise element on 'test' handler")
  // })
}
